import React, { useState } from "react";
import {
  useAdminData,
  usePlannerData,
  usePlannerDataV2,
} from "./hooks/useAdminData";
import { AdminTabType } from "./types";
import { RedisTab } from "./components/RedisTab";
import { PortfolioTab } from "./components/PortfolioTab";
import { UsersTab } from "./components/UsersTab";
import { InterestTab } from "./components/InterestTab";
import { AlarmTab } from "./components/AlarmTab";
import { PlannerTab } from "./components/PlannerTab";

// 글래스모피즘 스타일 정의
const glassStyles = `
  .glass-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1);
    padding: 32px;
    position: relative;
    overflow: hidden;
  }
  
  .glass-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
    opacity: 0;
    pointer-events: none;
  }
  
  .glass-kpi {
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid;
    border-radius: 20px;
    padding: 20px;
    position: relative;
    overflow: hidden;
  }
  
  .glass-sub-card {
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 16px;
    padding: 20px;
  }
  
  .glass-button {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    padding: 12px 24px;
    color: white;
    font-weight: 500;
    position: relative;
    overflow: hidden;
  }
  
  .glass-button::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  .glass-table-container {
    background: rgba(255, 255, 255, 0.02);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    overflow: hidden;
  }
  
  .glass-table-container::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  .glass-table-container::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  .glass-table-container::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    transition: background 0.3s ease;
  }
  
  .glass-table-container::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  .scrollable-table {
    overflow-y: auto;
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
  }
  
  .glow-text {
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  
  .floating {
    animation: float 6s ease-in-out infinite;
  }
`;

// 스타일을 head에 추가
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = glassStyles;
  document.head.appendChild(styleSheet);
}

function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTabType>("planner_v2");
  const { redisData, mysqlData, loading, error } = useAdminData();
  const {
    data: plannerData,
    loading: plannerLoading,
    error: plannerError,
  } = usePlannerData();
  const {
    data: plannerDataV2,
    loading: plannerV2Loading,
    error: plannerV2Error,
  } = usePlannerDataV2();

  if (loading || plannerLoading || plannerV2Loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-950 flex items-center justify-center">
        <div className="glass-card text-center">
          <div className="text-white text-xl font-medium glow-text">
            데이터를 불러오는 중...
          </div>
        </div>
      </div>
    );
  }

  if (error || plannerError || plannerV2Error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-950 flex items-center justify-center">
        <div className="glass-card text-center">
          <div className="text-red-400 text-xl font-medium glow-text">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!redisData || !mysqlData || !plannerData || !plannerDataV2) return null;

  const tabs = [
    { id: "planner_v2" as const, label: "플래너", icon: "📝" },
    { id: "portfolio" as const, label: "포폴", icon: "📊" },
    { id: "redis" as const, label: "Redis", icon: "⚡" },
    { id: "users" as const, label: "사용자", icon: "👥" },
    { id: "interest" as const, label: "관심종목", icon: "⭐" },
    { id: "alarm" as const, label: "알림", icon: "🔔" },
    { id: "planner" as const, label: "2025 재테크쇼", icon: "📝" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "redis":
        return <RedisTab data={redisData} />;
      case "portfolio":
        return <PortfolioTab data={redisData} />;
      case "users":
        return <UsersTab data={mysqlData} />;
      case "interest":
        return <InterestTab data={mysqlData} />;
      case "alarm":
        return <AlarmTab mysqlData={mysqlData} redisData={redisData} />;
      case "planner":
        return <PlannerTab data={plannerData} />;
      case "planner_v2":
        return <PlannerTab data={plannerDataV2} />;
      default:
        return <RedisTab data={redisData} />;
    }
  };

  return (
    <div className="z-[100] translate-y-[-90px] min-h-screen w-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-950 p-8 relative overflow-hidden">
      {/* 배경 장식 요소들 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-600/20 to-indigo-700/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-slate-600/20 to-blue-700/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-indigo-600/15 to-blue-600/15 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="max-w-screen-2xl mx-auto relative z-10">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-200 to-indigo-200 mb-2 drop-shadow-2xl">
            RW Admin
          </h1>
          <p className="text-lg text-white/70 mt-2 font-medium backdrop-blur-sm">
            운영 모니터링 • 실시간 지표 • 사용자 분석
          </p>
        </div>

        {/* 탭 네비게이션 */}
        <div className="mb-8">
          <div className="glass-sub-card p-2">
            <div className="flex gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    activeTab === tab.id
                      ? "bg-white/20 text-white border border-white/30 shadow-lg"
                      : "text-white/70 hover:text-white/90 hover:bg-white/10"
                  }`}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 탭 콘텐츠 */}
        {renderTabContent()}
      </div>
    </div>
  );
}

export default AdminPage;
